// Frontend service - don't use PlaidApi directly, use backend endpoints instead
import { supabase } from "../supabase/client";
import { demoService } from "../demo/demoService";
import { demoAPI } from "../api/demoApi";
import { isAbortError } from "../utils/abort";
import { debugThrottled } from "../../lib";
import { getCachedAccessToken } from "../supabase/authStorage";

// Base API URL - use environment variable or fallback to relative path
const resolveApiRoot = () => {
  if (typeof window !== "undefined") {
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";
    if (isLocal) {
      return "/api";
    }
  }
  return process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/api`
    : "/api";
};

const API_ROOT = resolveApiRoot();
const PLAID_REDIRECT_URI = process.env.REACT_APP_PLAID_REDIRECT_URI;

// Helper to get auth headers
const getAuthHeaders = async (): Promise<Record<string, string>> => {
  if (demoService.isDemo()) {
    return {
      "Content-Type": "application/json",
    };
  }
  debugThrottled(
    "plaidAuthHeaders:start",
    1500,
    "[DEBUG] Plaid getAuthHeaders start"
  );
  const cachedToken = getCachedAccessToken();
  if (cachedToken) {
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${cachedToken}`,
    };
  }
  let session = null;
  try {
    const {
      data: { session: activeSession },
    } = await supabase.auth.getSession();
    session = activeSession;
  } catch (error) {
    if (isAbortError(error)) {
      console.debug("[DEBUG] Plaid getAuthHeaders getSession aborted", error);
    } else {
      console.error("[DEBUG] Plaid getAuthHeaders getSession failed", error);
    }
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (session?.access_token) {
    headers["Authorization"] = `Bearer ${session.access_token}`;
  }

  return headers;
};

export interface PlaidInstitution {
  institution_id: string;
  name: string;
  logo: string | null;
  primary_color: string | null;
  url: string | null;
}

export interface PlaidAccount {
  account_id: string;
  item_id: string;
  balances: {
    available: number | null;
    current: number | null;
    limit: number | null;
  };
  mask: string;
  name: string;
  official_name: string | null;
  subtype: string;
  type: string;
  is_shared?: boolean;
  isShared?: boolean;
  holder_category?: "personal" | "business" | null;
  institution?: PlaidInstitution | null;
  // "manual" for non-Plaid accounts the user added directly (e.g. Cash App).
  // Absent/"plaid" for Plaid-linked accounts.
  source?: "plaid" | "manual";
}

export interface ManualAccountInput {
  name: string;
  balance?: number | string | null;
  provider?: "cash_app" | "venmo" | "lyft_direct" | "uber_card" | "other";
  type?: string;
  subtype?: string;
  mask?: string;
  officialName?: string;
}

export type ConnectionMethod = "plaid_link" | "stripe_oauth" | "manual_csv";

export interface ConnectionNextAction {
  type: ConnectionMethod;
  provider: string | null;
  startUrl: string | null;
  instructions: string;
  csvSource: string | null;
  mcpTool: string;
}

export interface ConnectionTargetStatus {
  id: string;
  label: string;
  method: ConnectionMethod;
  provider: string | null;
  connected: boolean;
  accountCount: number;
  matchInstitutionNames: string[] | null;
  nextAction: ConnectionNextAction | null;
}

export interface ConnectionStatusResponse {
  targets: ConnectionTargetStatus[];
  plaidItemCount: number;
  primeAgentConnected: boolean;
  summary: { connected: number; pending: number; total: number };
}

// Synthetic connection status for demo mode, so the Connect checklist renders
// without a backend/auth. Mirrors the shape of GET /api/connections/status.
function buildDemoConnectionStatus(): ConnectionStatusResponse {
  const mk = (
    id: string,
    label: string,
    method: ConnectionMethod,
    connected: boolean,
    extra?: Partial<ConnectionTargetStatus>
  ): ConnectionTargetStatus => ({
    id,
    label,
    method,
    provider: extra?.provider ?? null,
    connected,
    accountCount: connected ? 1 : 0,
    matchInstitutionNames: extra?.matchInstitutionNames ?? null,
    nextAction: connected
      ? null
      : {
          type: method,
          provider: extra?.provider ?? null,
          startUrl: method === "stripe_oauth" ? "/api/stripe/connect" : null,
          instructions:
            extra?.nextAction?.instructions ||
            "Connect this account to import its transactions.",
          csvSource: extra?.nextAction?.csvSource ?? null,
          mcpTool:
            method === "manual_csv"
              ? "import_wallet_csv"
              : method === "stripe_oauth"
                ? "start_stripe_connect"
                : "start_plaid_link",
        },
  });
  const targets: ConnectionTargetStatus[] = [
    mk("chase", "Chase", "plaid_link", true),
    mk("paypal", "PayPal", "plaid_link", true),
    mk("onpoint", "OnPoint Community CU", "plaid_link", false),
    mk("selco", "SELCO Community CU", "plaid_link", false),
    mk("stripe", "Stripe (Payment Processing)", "stripe_oauth", false),
    mk("cash_app", "Cash App", "manual_csv", false, {
      provider: "cash_app",
      nextAction: {
        instructions:
          "In Cash App, open Activity, export a CSV, then add the Cash App wallet and upload it.",
        csvSource: "Cash App → Activity → export CSV",
      } as ConnectionNextAction,
    }),
    mk("venmo", "Venmo", "manual_csv", false, { provider: "venmo" }),
    mk("lyft_direct", "Lyft Direct Wallet Card", "manual_csv", false, {
      provider: "lyft_direct",
    }),
    mk("uber_card", "Uber Card", "manual_csv", false, {
      provider: "uber_card",
    }),
  ];
  const connected = targets.filter((t) => t.connected).length;
  return {
    targets,
    plaidItemCount: 2,
    primeAgentConnected: false,
    summary: {
      connected,
      pending: targets.length - connected,
      total: targets.length,
    },
  };
}

export interface PlaidTransaction {
  account_id: string;
  amount: number;
  date: string;
  datetime?: string | null;
  authorized_datetime?: string | null;
  name: string;
  merchant_name?: string;
  original_description?: string;
  internal_category: string[];
  category?: string | string[];
  transaction_id: string;
  transaction_type?: string;
  account_owner?: string;
  source?: "plaid" | "manual";
  is_shared?: boolean;
  isShared?: boolean;
  location?: {
    address?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
  payment_meta?: {
    reference_number?: string;
    ppd_id?: string;
    payee?: string;
  };
}

export interface PlaidTransactionsCacheStatus {
  lastSyncedAt: string | null;
  refreshing: boolean;
  cacheReadFailed?: boolean;
}

export interface CSVTransaction {
  date: string;
  amount: number;
  name: string;
  merchant_name?: string;
  category?: string;
}

export interface ImportResult {
  imported: number;
  skipped: number;
  message: string;
}

export interface DeleteAllAccountsResult {
  success: boolean;
  message: string;
  summary?: {
    plaidItemsDeleted: number;
    stripeConnectionsDeleted: number;
  };
}

export interface ImportHistoryEntry {
  id: string;
  user_id: string;
  account_id: string;
  status: "success" | "failed" | string;
  imported_count: number;
  skipped_count: number;
  total_count: number;
  source?: string | null;
  error_message?: string | null;
  created_at: string;
  oldest_date?: string | null;
  newest_date?: string | null;
}

const ACCOUNTS_CACHE_KEY = "plaid_accounts_cache_session";
const ACCOUNTS_CACHE_KEY_FALLBACK = "plaid_accounts_cache_fallback";
const ACCOUNTS_CACHE_TTL_MS = 10 * 60 * 1000;

interface CachedAccounts {
  data: PlaidAccount[];
  timestamp: number;
}

const readAccountsCache = (): PlaidAccount[] => {
  try {
    const cached =
      typeof window !== "undefined"
        ? window.sessionStorage.getItem(ACCOUNTS_CACHE_KEY)
        : null;
    if (!cached) return [];
    const parsed = JSON.parse(cached) as CachedAccounts;
    if (Date.now() - parsed.timestamp > ACCOUNTS_CACHE_TTL_MS) {
      return [];
    }
    return Array.isArray(parsed.data) ? parsed.data : [];
  } catch (error) {
    console.debug("[DEBUG] Failed to read accounts cache", error);
    return [];
  }
};

const readAccountsFallback = (): PlaidAccount[] => {
  try {
    const cached =
      typeof window !== "undefined"
        ? window.localStorage.getItem(ACCOUNTS_CACHE_KEY_FALLBACK)
        : null;
    if (!cached) return [];
    const parsed = JSON.parse(cached) as CachedAccounts;
    if (Date.now() - parsed.timestamp > ACCOUNTS_CACHE_TTL_MS) {
      return [];
    }
    return Array.isArray(parsed.data) ? parsed.data : [];
  } catch (error) {
    console.debug("[DEBUG] Failed to read fallback accounts cache", error);
    return [];
  }
};

const writeAccountsCache = (accounts: PlaidAccount[]) => {
  try {
    if (typeof window === "undefined") return;
    const payload: CachedAccounts = {
      data: accounts,
      timestamp: Date.now(),
    };
    window.sessionStorage.setItem(ACCOUNTS_CACHE_KEY, JSON.stringify(payload));
    window.localStorage.setItem(
      ACCOUNTS_CACHE_KEY_FALLBACK,
      JSON.stringify(payload)
    );
  } catch (error) {
    console.debug("[DEBUG] Failed to write accounts cache", error);
  }
};

const normalizeManualTransactions = (
  transactions: PlaidTransaction[]
): PlaidTransaction[] => {
  const statsByAccount = new Map<
    string,
    { positive: number; negative: number; total: number }
  >();

  transactions.forEach((txn) => {
    if (txn.source !== "manual") return;
    const amount =
      typeof txn.amount === "number"
        ? txn.amount
        : parseFloat(String(txn.amount));
    if (!Number.isFinite(amount) || amount === 0) return;
    const accountId = txn.account_id || "manual";
    const stats = statsByAccount.get(accountId) || {
      positive: 0,
      negative: 0,
      total: 0,
    };
    if (amount > 0) stats.positive += 1;
    if (amount < 0) stats.negative += 1;
    stats.total += 1;
    statsByAccount.set(accountId, stats);
  });

  const accountsToFlip = new Set<string>();
  statsByAccount.forEach((stats, accountId) => {
    if (stats.total < 5) return;
    const negativeShare = stats.negative / stats.total;
    if (negativeShare >= 0.6) {
      accountsToFlip.add(accountId);
    }
  });

  if (accountsToFlip.size === 0) {
    console.debug("[DEBUG] Manual transaction normalization skipped", {
      accountsChecked: statsByAccount.size,
    });
    return transactions;
  }

  console.log("[DEBUG] Normalizing manual transaction amounts", {
    accountsToFlip: Array.from(accountsToFlip),
    stats: Array.from(statsByAccount.entries()),
  });

  return transactions.map((txn) => {
    if (txn.source !== "manual") return txn;
    const accountId = txn.account_id || "manual";
    if (!accountsToFlip.has(accountId)) return txn;
    const amount =
      typeof txn.amount === "number"
        ? txn.amount
        : parseFloat(String(txn.amount));
    if (!Number.isFinite(amount) || amount === 0) return txn;
    return {
      ...txn,
      amount: -amount,
    };
  });
};

export const plaidService = {
  // Connection status for the "Connect your accounts" checklist. Returns the
  // target catalog with connected/pending state + next actions.
  getConnectionStatus: async (): Promise<ConnectionStatusResponse> => {
    if (demoService.isDemo()) {
      return buildDemoConnectionStatus();
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/connections/status`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to load connection status (${response.status})`);
    }
    return response.json();
  },

  // Create link token for Plaid Link initialization
  // Credentials are managed at the system level (backend .env)
  createLinkToken: async (opts?: {
    itemId?: string;
    accountSelection?: boolean;
  }): Promise<string> => {
    if (demoService.isDemo()) {
      return "";
    }
    const fallbackRedirectUri = `${window.location.origin}/accounts`;
    const redirectUri = PLAID_REDIRECT_URI || fallbackRedirectUri;
    const headers = await getAuthHeaders();
    console.log("[DEBUG] Creating Plaid link token", {
      hasRedirectUri: Boolean(redirectUri),
      redirectUri,
      itemId: opts?.itemId || null,
      accountSelection: Boolean(opts?.accountSelection),
    });
    const response = await fetch(`${API_ROOT}/plaid/create-link-token`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        redirect_uri: redirectUri,
        ...(opts?.itemId ? { item_id: opts.itemId } : {}),
        ...(opts?.accountSelection ? { account_selection: true } : {}),
      }),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      const errorBody = await response.text();
      console.error("[DEBUG] Plaid link token request failed", {
        status: response.status,
        statusText: response.statusText,
        errorBody,
      });
      throw new Error("Failed to create link token");
    }

    const data = await response.json();
    console.log("[DEBUG] Plaid link token created", {
      hasToken: Boolean(data.link_token),
    });
    return data.link_token;
  },

  itemsNeedingUpdate: async (): Promise<
    Array<{
      item_id: string;
      institution_id: string | null;
      institution_name: string | null;
      error_code: string;
    }>
  > => {
    if (demoService.isDemo()) return [];
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/plaid/items-needing-update`, {
      headers,
    });
    if (!response.ok) return [];
    const data = await response.json();
    return data.items || [];
  },

  // Exchange public token for access token
  // Credentials are managed at the system level (backend .env)
  exchangePublicToken: async (public_token: string): Promise<void> => {
    if (demoService.isDemo()) {
      return;
    }
    const headers = await getAuthHeaders();
    console.log("[DEBUG] Exchanging Plaid public token");
    const response = await fetch(`${API_ROOT}/plaid/exchange-public-token`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        public_token,
      }),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      const errorBody = await response.text();
      console.error("[DEBUG] Plaid public token exchange failed", {
        status: response.status,
        statusText: response.statusText,
        errorBody,
      });
      throw new Error("Failed to exchange public token");
    }
    console.log("[DEBUG] Plaid public token exchange succeeded");
  },

  // Get accounts
  // Credentials are managed at the system level (backend .env)
  getAccounts: async (): Promise<PlaidAccount[]> => {
    if (demoService.isDemo()) {
      return demoAPI.getAccounts() as PlaidAccount[];
    }
    try {
      const headers = await getAuthHeaders();
      console.log("Requesting Plaid accounts", {
        hasAuth: Boolean(headers.Authorization),
      });
      const response = await fetch(`${API_ROOT}/plaid/accounts`, { headers });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          if (window.location.pathname !== "/login")
            console.warn(
              "Auth required; suppressing hard redirect during request."
            );
        }
        const errorBody = await response.text();
        console.error("Plaid accounts request failed", {
          status: response.status,
          statusText: response.statusText,
          errorBody,
        });
        throw new Error("Failed to fetch accounts");
      }

      const data = await response.json();
      const accounts = data.accounts || [];
      writeAccountsCache(accounts);
      console.log("Plaid accounts response", { count: accounts.length });
      return accounts;
    } catch (error) {
      if (isAbortError(error)) {
        console.debug("[DEBUG] Plaid getAccounts aborted", error);
        return [];
      }
      throw error;
    }
  },

  getAccountsCached: async (): Promise<PlaidAccount[]> => {
    if (demoService.isDemo()) {
      return demoAPI.getAccounts() as PlaidAccount[];
    }
    const cached = readAccountsCache();
    if (cached.length > 0) return cached;
    return readAccountsFallback();
  },

  refreshAccountsCache: async (): Promise<PlaidAccount[]> => {
    const accounts = await plaidService.getAccounts();
    writeAccountsCache(accounts);
    return accounts;
  },

  // Get transactions
  getTransactions: async (
    startDate: string,
    endDate: string,
    environment?: string,
    clientId?: string
  ): Promise<PlaidTransaction[]> => {
    if (demoService.isDemo()) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      return (demoAPI.getTransactions() as PlaidTransaction[]).filter((tx) => {
        const txDate = new Date(tx.date);
        return txDate >= start && txDate <= end;
      });
    }
    const params = new URLSearchParams();
    params.append("start_date", startDate);
    params.append("end_date", endDate);
    if (environment) params.append("environment", environment);
    if (clientId) params.append("clientId", clientId);

    const headers = await getAuthHeaders();
    const requestUrl = `${API_ROOT}/plaid/transactions?${params.toString()}`;
    console.debug("[DEBUG] Plaid transactions request", {
      requestUrl,
      startDate,
      endDate,
      hasAuth: Boolean(headers.Authorization),
    });
    const response = await fetch(requestUrl, { headers });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to fetch transactions");
    }

    const data = await response.json();
    console.debug("[DEBUG] Plaid transactions response", {
      count: data.transactions?.length ?? 0,
    });
    return normalizeManualTransactions(data.transactions || []);
  },

  getTransactionsWithCacheStatus: async (
    startDate: string,
    endDate: string,
    environment?: string,
    clientId?: string
  ): Promise<{
    transactions: PlaidTransaction[];
    cacheStatus: PlaidTransactionsCacheStatus;
  }> => {
    if (demoService.isDemo()) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const transactions = (
        demoAPI.getTransactions() as PlaidTransaction[]
      ).filter((tx) => {
        const txDate = new Date(tx.date);
        return txDate >= start && txDate <= end;
      });
      return {
        transactions,
        cacheStatus: { lastSyncedAt: null, refreshing: false },
      };
    }

    const params = new URLSearchParams();
    params.append("start_date", startDate);
    params.append("end_date", endDate);
    if (environment) params.append("environment", environment);
    if (clientId) params.append("clientId", clientId);

    const headers = await getAuthHeaders();
    const requestUrl = `${API_ROOT}/plaid/transactions?${params.toString()}`;
    console.debug("[DEBUG] Plaid transactions request (status)", {
      requestUrl,
      startDate,
      endDate,
      hasAuth: Boolean(headers.Authorization),
    });
    const response = await fetch(requestUrl, { headers });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to fetch transactions");
    }

    const data = await response.json();
    console.debug("[DEBUG] Plaid transactions response (status)", {
      count: data.transactions?.length ?? 0,
      refreshing: data.cacheStatus?.refreshing,
    });

    return {
      transactions: normalizeManualTransactions(data.transactions || []),
      cacheStatus: data.cacheStatus || {
        lastSyncedAt: null,
        refreshing: false,
      },
    };
  },

  refreshTransactionsCache: async (options?: {
    force?: boolean;
    wait?: boolean;
    environment?: string;
    clientId?: string;
  }): Promise<{ status: string }> => {
    if (demoService.isDemo()) {
      return { status: "demo" };
    }
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (options?.force) params.append("force", "true");
    if (options?.wait) params.append("wait", "true");
    if (options?.environment) params.append("environment", options.environment);
    if (options?.clientId) params.append("clientId", options.clientId);

    const requestUrl = `${API_ROOT}/plaid/transactions/cache/refresh?${params.toString()}`;
    console.debug("[DEBUG] Plaid cache refresh request", {
      requestUrl,
      options,
    });

    const response = await fetch(requestUrl, {
      method: "POST",
      headers,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to refresh transactions cache");
    }

    const data = await response.json();
    console.debug("[DEBUG] Plaid cache refresh response", data);
    return data;
  },

  getTransactionsCacheStatus: async (
    environment?: string
  ): Promise<PlaidTransactionsCacheStatus> => {
    if (demoService.isDemo()) {
      return { lastSyncedAt: null, refreshing: false };
    }
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (environment) params.append("environment", environment);
    const requestUrl = `${API_ROOT}/plaid/transactions/cache/status?${params.toString()}`;
    console.debug("[DEBUG] Plaid cache status request", { requestUrl });
    const response = await fetch(requestUrl, { headers });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to fetch transactions cache status");
    }

    const data = await response.json();
    console.debug("[DEBUG] Plaid cache status response", data);
    return {
      lastSyncedAt: data.lastSyncedAt || null,
      refreshing: Boolean(data.refreshing),
      cacheReadFailed: Boolean(data.cacheReadFailed),
    };
  },

  // Delete a Plaid connection
  deleteConnection: async (itemId: string): Promise<void> => {
    if (demoService.isDemo()) {
      return;
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/plaid/connections/${itemId}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to delete connection");
    }
  },

  deleteAllAccounts: async (
    password: string
  ): Promise<DeleteAllAccountsResult> => {
    if (demoService.isDemo()) {
      return {
        success: true,
        message: "Demo mode: account deletion skipped",
      };
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/accounts`, {
      method: "DELETE",
      headers,
      body: JSON.stringify({ password }),
    });

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error("Invalid account password");
      }
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to delete all accounts");
    }

    return response.json();
  },

  // Get sync status for all connected accounts
  getSyncStatus: async (): Promise<SyncStatusResponse> => {
    if (demoService.isDemo()) {
      return { items: [], manualTransactionCounts: {} };
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/plaid/sync-status`, {
      headers,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to fetch sync status");
    }

    const data = await response.json();
    return {
      items: data.items,
      manualTransactionCounts: data.manualTransactionCounts || {},
    };
  },

  // Force refresh transactions for an item
  refreshTransactions: async (itemId: string): Promise<{ message: string }> => {
    if (demoService.isDemo()) {
      return { message: "Demo mode: refresh skipped" };
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/plaid/refresh/${itemId}`, {
      method: "POST",
      headers,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to refresh transactions");
    }

    return response.json();
  },

  // Get all custom account names
  getCustomNames: async (): Promise<Record<string, string>> => {
    if (demoService.isDemo()) {
      return {};
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/accounts/custom-names`, {
      headers,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to fetch custom names");
    }

    const data = await response.json();
    return data.names;
  },

  // Set custom name for an account
  setCustomName: async (
    accountId: string,
    name: string
  ): Promise<{ name: string }> => {
    if (demoService.isDemo()) {
      return { name };
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/accounts/${accountId}/name`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ name }),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to set custom name");
    }

    return response.json();
  },

  // Delete custom name for an account
  deleteCustomName: async (accountId: string): Promise<void> => {
    if (demoService.isDemo()) {
      return;
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/accounts/${accountId}/name`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to delete custom name");
    }
  },

  // Import transactions from CSV data
  importTransactions: async (
    accountId: string,
    transactions: CSVTransaction[]
  ): Promise<ImportResult> => {
    if (demoService.isDemo()) {
      return {
        imported: 0,
        skipped: transactions.length,
        message: "Demo mode: import skipped",
      };
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/accounts/${accountId}/import`, {
      method: "POST",
      headers,
      body: JSON.stringify({ transactions }),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to import transactions");
    }

    return response.json();
  },

  // Get CSV import history for an account
  getImportHistory: async (
    accountId: string
  ): Promise<ImportHistoryEntry[]> => {
    if (demoService.isDemo()) {
      return [];
    }
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_ROOT}/accounts/${accountId}/import-history`,
      { headers }
    );

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to fetch import history");
    }

    const data = await response.json();
    return data.history || [];
  },

  // Latest manual-import timestamp per account_id, used (alongside Plaid sync
  // times) to render the real "Last Imported" date on the Accounts page.
  getLatestImportTimestamps: async (): Promise<Record<string, string>> => {
    if (demoService.isDemo()) {
      return {};
    }
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${API_ROOT}/accounts/import-history/latest`,
        { headers }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch latest import timestamps");
      }
      const data = await response.json();
      return data.latest || {};
    } catch (err) {
      console.warn("[plaidService] getLatestImportTimestamps failed", err);
      return {};
    }
  },

  // Create a manual (non-Plaid) account, e.g. Cash App.
  createManualAccount: async (
    input: ManualAccountInput
  ): Promise<PlaidAccount> => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/accounts/manual`, {
      method: "POST",
      headers,
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || "Failed to create manual account");
    }
    const data = await response.json();
    return data.account as PlaidAccount;
  },

  // Update a manual account's name and/or balance.
  updateManualAccount: async (
    accountId: string,
    updates: { name?: string; balance?: number | string | null; mask?: string }
  ): Promise<PlaidAccount> => {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_ROOT}/accounts/manual/${encodeURIComponent(accountId)}`,
      {
        method: "PATCH",
        headers,
        body: JSON.stringify(updates),
      }
    );
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error || "Failed to update manual account");
    }
    const data = await response.json();
    return data.account as PlaidAccount;
  },

  // Delete a manual account (and its imported transactions).
  deleteManualAccount: async (accountId: string): Promise<void> => {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_ROOT}/accounts/manual/${encodeURIComponent(accountId)}`,
      { method: "DELETE", headers }
    );
    if (!response.ok) {
      throw new Error("Failed to delete manual account");
    }
  },
};

export interface PlaidSyncStatus {
  item_id: string;
  institution_name: string;
  institution_id: string;
  consent_expiration_time: string | null;
  update_type: string;
  transactions: {
    total_count: number;
    oldest_date: string | null;
    newest_date: string | null;
    last_successful_update: string | null;
    last_failed_update: string | null;
  };
  last_webhook: string | null;
  error?: string;
}

export interface ManualTransactionCount {
  count: number;
  oldest_date: string | null;
  newest_date: string | null;
}

export interface AccountTransactionStats {
  total_count: number;
  oldest_date: string | null;
  newest_date: string | null;
}

export interface SyncStatusResponse {
  items: PlaidSyncStatus[];
  manualTransactionCounts: Record<string, ManualTransactionCount>;
  accountTransactionStats?: Record<string, AccountTransactionStats>;
}

// Organization types
export interface Organization {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  color: string;
  created_at: string;
  updated_at: string;
}

export interface OrganizationInput {
  name: string;
  description?: string;
  color?: string;
}

export const organizationService = {
  // Get all organizations
  getAll: async (): Promise<Organization[]> => {
    if (demoService.isDemo()) {
      return [];
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/organizations`, { headers });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to fetch organizations");
    }

    return response.json();
  },

  // Create a new organization
  create: async (org: OrganizationInput): Promise<Organization> => {
    if (demoService.isDemo()) {
      throw new Error("Cannot create organizations in demo mode");
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/organizations`, {
      method: "POST",
      headers,
      body: JSON.stringify(org),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to create organization");
    }

    return response.json();
  },

  // Update an organization
  update: async (id: string, org: OrganizationInput): Promise<Organization> => {
    if (demoService.isDemo()) {
      throw new Error("Cannot update organizations in demo mode");
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/organizations/${id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify(org),
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to update organization");
    }

    return response.json();
  },

  // Delete an organization
  delete: async (id: string): Promise<void> => {
    if (demoService.isDemo()) {
      return;
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/organizations/${id}`, {
      method: "DELETE",
      headers,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to delete organization");
    }
  },

  // Assign organization to an account
  assignToAccount: async (
    accountId: string,
    organizationId: string | null
  ): Promise<void> => {
    if (demoService.isDemo()) {
      return;
    }
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_ROOT}/accounts/${accountId}/organization`,
      {
        method: "PUT",
        headers,
        body: JSON.stringify({ organizationId }),
      }
    );

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to assign organization");
    }
  },

  // Get account-to-organization mapping
  getAccountOrganizations: async (): Promise<Record<string, string>> => {
    if (demoService.isDemo()) {
      return {};
    }
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_ROOT}/accounts/organizations`, {
      headers,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        if (window.location.pathname !== "/login")
          console.warn(
            "Auth required; suppressing hard redirect during request."
          );
      }
      throw new Error("Failed to fetch account organizations");
    }

    const data = await response.json();
    return data.mapping;
  },
};
